import { useEffect, useMemo, useState } from "react";
import API from "../../lib/api";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { isoDay } from "./hrUtils";

const DOW = ["M", "T", "W", "T", "F", "S", "S"];

/**
 * Month grid of the team's leave. Working days taken by someone else are blocked (hatched, unclickable);
 * your own leave is coloured; bank holidays and weekends are dimmed. Click start then end to select a range.
 */
export const LeaveCalendar = ({ value, onChange, selectable = true, refreshKey, onInfo }) => {
  const [month, setMonth] = useState(() => isoDay(new Date()).slice(0, 7));
  const [blocked, setBlocked] = useState({ days: [], bank_holidays: [], sick_days: {} });
  const today = isoDay(new Date());

  const first = `${month}-01`;
  const lastDay = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const last = `${month}-${String(lastDay).padStart(2, "0")}`;

  useEffect(() => {
    API.get(`/hr/leave/blocked?start=${first}&end=${last}`).then(({ data }) => { setBlocked(data); onInfo?.(data); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, last, refreshKey]);

  const byDate = useMemo(() => Object.fromEntries((blocked.days || []).map((d) => [d.date, d])), [blocked]);
  const bank = useMemo(() => new Set(blocked.bank_holidays || []), [blocked]);
  const offset = (new Date(`${first}T00:00:00`).getDay() + 6) % 7;
  const cells = [...Array(offset).fill(null), ...Array.from({ length: lastDay }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];

  const shift = (n) => { const d = new Date(`${first}T00:00:00`); d.setMonth(d.getMonth() + n); setMonth(isoDay(d).slice(0, 7)); };
  const inRange = (d) => value?.start && value?.end && d >= value.start && d <= value.end;
  const pick = (d, cell) => {
    if (!selectable) return;
    if (cell && cell.others > 0 && !cell.mine) return;
    if (!value?.start || value.end) onChange({ start: d, end: null });
    else if (d < value.start) onChange({ start: d, end: value.start });
    else onChange({ start: value.start, end: d });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3 select-none" data-testid="hr-leave-calendar">
      <div className="flex items-center justify-between mb-2">
        <button className="p-1 rounded hover:bg-muted" onClick={() => shift(-1)} data-testid="hr-cal-prev"><CaretLeft /></button>
        <p className="text-sm font-semibold" data-testid="hr-cal-month">{new Date(`${first}T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</p>
        <button className="p-1 rounded hover:bg-muted" onClick={() => shift(1)} data-testid="hr-cal-next"><CaretRight /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground mb-1">{DOW.map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <span key={`e${i}`} />;
          const dow = i % 7;
          const cell = byDate[d];
          const weekend = dow >= 5;
          const isBank = bank.has(d);
          const taken = cell && cell.others > 0 && !cell.mine;
          const mine = cell?.mine;
          const sick = blocked.sick_days?.[d];
          const sel = inRange(d) || value?.start === d;
          const past = d < today;
          const who = taken ? (cell.first_names || []).join(", ") : "";
          let cls = "relative h-12 rounded-md text-xs flex flex-col items-center justify-center gap-0.5 border transition-colors px-0.5 ";
          if (weekend || isBank) cls += "border-transparent text-muted-foreground/40 bg-muted/30 ";
          else if (taken) cls += "border-border text-muted-foreground bg-[repeating-linear-gradient(135deg,transparent,transparent_4px,rgba(100,116,139,.18)_4px,rgba(100,116,139,.18)_8px)] cursor-not-allowed ";
          else if (mine) cls += cell.my_status === "pending" ? "border-amber-300 bg-amber-100 text-amber-900 " : "border-emerald-300 bg-emerald-100 text-emerald-900 ";
          else cls += past ? "border-transparent text-muted-foreground/60 " : "border-border hover:border-primary hover:bg-primary/5 cursor-pointer ";
          if (sel && !weekend && !isBank) cls += "!bg-primary !text-primary-foreground !border-primary ";
          const title = taken ? (cell.names?.length ? cell.names.join(", ") : "Taken — someone else is off") : mine ? `Your leave (${cell.my_status})` : isBank ? "Bank holiday" : sick ? `Sick: ${sick.join(", ")}` : "";
          return (
            <button key={d} type="button" title={title} disabled={weekend || isBank || taken || (!selectable)} onClick={() => pick(d, cell)} className={cls}
              data-testid={`hr-cal-day-${d}`} data-state={taken ? "taken" : mine ? "mine" : sel ? "selected" : "free"}>
              <span className="leading-none">{Number(d.slice(8, 10))}</span>
              {who && <span className="block max-w-full truncate text-[10px] leading-none font-medium text-foreground/80" data-testid={`hr-cal-who-${d}`}>{who}</span>}
              {mine && <span className="block text-[10px] leading-none font-medium opacity-80">You</span>}
              {sick && <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-red-500" />}
              {d === today && <span className="absolute bottom-0.5 h-0.5 w-3 rounded-full bg-current opacity-60" />}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm bg-emerald-100 border border-emerald-300" /> Your leave</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm bg-amber-100 border border-amber-300" /> Awaiting approval</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm border border-border bg-[repeating-linear-gradient(135deg,transparent,transparent_2px,rgba(100,116,139,.3)_2px,rgba(100,116,139,.3)_4px)]" /> Taken by someone else</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm bg-muted/60" /> Weekend / bank holiday</span>
      </div>
    </div>
  );
};
